import { useEffect, useMemo, useState } from "react";
import {
  collection,
  collectionGroup,
  doc,
  documentId,
  onSnapshot,
  orderBy,
  query,
  where,
  limit as fbLimit,
  getCountFromServer,
  getDocs,
  type DocumentData,
  type Query,
  type Timestamp,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { buildSearchTokens, matchesAllWords, searchToken } from "@chbapark/domain/search";
import { db, WORKSPACE_ID } from "./firebase";

export interface Person extends DocumentData {
  id: string;
  displayName?: string;
  phones?: { value?: string; normalized?: string; primary?: boolean }[];
  emails?: { value?: string; primary?: boolean }[];
  childrenAges?: number[];
  /** IDs into `tag_defs`, not names — renaming a tag must not rewrite people. */
  tagIds?: string[];
  businessServiceTagIds?: string[];
  householdId?: string;
  householdName?: string;
  /**
   * Whether the household this person belongs to is a family or a building.
   *
   * The imported `household_id` grouped people by street address — one of them
   * holds twenty-two people from five families. Anything that says "family" has
   * to check this first, or it prints somebody's neighbours as their relatives.
   */
  householdKind?: "family" | "address_group";
  /** Everybody in the grouping, family or building alike. */
  householdMemberCount?: number;
  /** Only the people this person is actually related to. 1 means "just them" —
   *  the honest answer where the grouping is an address. */
  familyMemberCount?: number;
  familySurname?: string;
  /**
   * People a human has said are *not* related to this one.
   *
   * Relatives inside an imported address group are derived from a shared
   * surname, and two unrelated families with the same surname in one building is
   * the case no rule can separate. Written on both people, and honoured by every
   * later recomputation — a correction that expires is not a correction.
   */
  familyExcludedPersonIds?: string[];
  /** This record names a couple, not a person — "ראם ושמרית כהן". A real entry
   *  in the source data, marked so it is not mistaken for one human being. */
  isCoupleRecord?: boolean;
  /** Other people listing the same phone number. 441 numbers in the imported
   *  data are shared, which is why the same man could appear three times. */
  sharesPhoneWithPersonIds?: string[];
  householdRole?: string;
  isChild?: boolean;
  address?: { formatted?: string; street?: string; houseNumber?: string; city?: string; neighborhood?: string };
  organization?: string;
  gregorianBirthDate?: string;
  education?: { school?: string | null; grade?: string | null };
  nextStepText?: string;
  /** ISO instant of the last real conversation, written by `logInteraction`. */
  lastMeaningfulContactAt?: string;
  lastInteractionAt?: string;
  /** Legacy demo field. Real recency comes from `lastMeaningfulContactAt`. */
  lastContactDaysAgo?: number;
  /**
   * Giving history, rolled up by `recomputeDonorTotals`.
   *
   * Verified and imported stay separate because an imported figure is what a
   * spreadsheet said and a verified one is confirmation from a payment provider.
   * That distinction matters for a receipt.
   *
   * It does **not** matter for "who gives the most", and this comment used to
   * say adding them produced a number nobody could defend. The product owner
   * answered that fairly: defend it to whom, and what use is a figure nobody can
   * sort by? The sum is now stored as `donationTotalAllMinor` for ranking, and
   * the person's own screen still shows the two components, so nothing is merged
   * away — only made usable.
   */
  donationTotalVerifiedMinor?: number;
  donationTotalImportedMinor?: number;
  /** The stored sum of both, written by recomputeDonorTotals purely so the list
   *  can be ordered — Firestore cannot sort by an expression. */
  donationTotalAllMinor?: number;
  donationCount?: number;
  firstDonationAt?: string;
  lastDonationAt?: string;
  hasStandingOrder?: boolean;
  /** 0–100. Ranks how clearly a relationship is documented, not how much
   *  somebody is worth: frequency counts for more than size. */
  importanceScore?: number;
  consents?: Record<string, { status?: string }>;
  source?: { type?: string; sources?: string[] };
  owners?: string[];
  isDemo?: boolean;
}

/** Whole days since the last real conversation, or null if there has not been
 *  one. Computed here rather than stored, so it cannot go stale overnight. */
export function daysSinceContact(p: Person): number | null {
  const iso = p.lastMeaningfulContactAt;
  if (typeof iso !== "string" || iso === "") {
    return typeof p.lastContactDaysAgo === "number" ? p.lastContactDaysAgo : null;
  }
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((Date.now() - then) / 86_400_000));
}

/**
 * One card on Today.
 *
 * Every field that explains the card is stored on it rather than recomputed:
 * `reasonText`, `reasonCodes`, `score`, `ruleId`, `ruleVersion`, `computedAt`.
 * Spec §4.2(5) requires exactly that — a recommendation has to be explainable
 * and auditable after the fact, and a card whose reasoning only existed at
 * compute time cannot be either.
 */
export interface Recommendation extends DocumentData {
  id: string;
  dayKey?: string;
  rank?: number;
  status?: "ready" | "in_progress" | "done" | "snoozed" | "dismissed" | "reassigned" | "retired" | "expired";
  /** Which rule produced it, and the version of that rule's logic. */
  ruleId?: string;
  ruleVersion?: number;
  score?: number;
  reasonCodes?: string[];
  reasonText?: string;
  metaText?: string;
  /** Other reasons this person is on the list today. Separate from metaText so
   *  a yahrzeit card cannot end its subtitle with the word "birthday". */
  alsoBecause?: string[];
  personId?: string;
  personName?: string;
  personPhone?: string;
  /** Set instead of a person on a card about a gathering — "mark who came". Every
   *  other card names somebody to contact; this one names something to do. */
  eventId?: string | null;
  eventTitle?: string | null;
  /** What to do, and through which channel. */
  action?: "whatsapp" | "call" | "reply" | "visit" | "rollcall";
  channel?: "whatsapp" | "phone" | "in_person";
  severity?: "warm" | "attention" | "urgent";
  sourceRef?: { type?: string; id?: string } | null;
  /** Which WhatsApp message fits the card, and the facts that fill it in. The
   *  wording itself is in the message templates (`messages.ts`). */
  messageKey?: string | null;
  messageVars?: Record<string, string>;
  dismissReason?: string | null;
  assignedTo?: string;
  reassignedFrom?: string;
  isDemo?: boolean;
}

/** Jerusalem, so "today" means the same thing wherever the phone thinks it is. */
export function todayKey(now = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Jerusalem",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

export interface InboxItem extends DocumentData {
  id: string;
  type?: string;
  status?: string;
  personName?: string;
  personPhone?: string;
  sourceText?: string;
  receivedText?: string;
  title?: string;
  detailLines?: string[];
  duplicateOf?: { name?: string; phone?: string };
  candidatePersonIds?: string[];
  isDemo?: boolean;
  /** Where a server-created item came from, e.g. a Google Contacts import. */
  source?: { provider?: string; ownerUid?: string; resourceName?: string };
  /** Server-created items keep their detail here rather than as flat fields. */
  evidence?: {
    fullName?: string | null;
    phones?: string[];
    emails?: string[];
    organization?: string | null;
    note?: string | null;
    /** The contact's Google labels, if it has any. A hint, not a classification. */
    labels?: string[];
    sourceUpdatedAt?: string | null;
    reasons?: string[];
    donationId?: string;
    amountMinor?: number | null;
    payerName?: string | null;
    purpose?: string | null;
    collectedAt?: string | null;
    providerDocumentId?: string | null;
    /** Why the processor refused to decide. Written by the intake processor on a
     *  `possible_duplicate`, and the only thing that explains the card. */
    reason?: string | null;
    /** Which identifiers disagreed — `["phone","email"]` and so on. */
    identityKinds?: string[];
    /** What kind of website form this was, on an `unhandled_submission`. */
    eventType?: string | null;
    page?: string | null;
    form?: string | null;
    /**
     * The whole submission, as it arrived.
     *
     * Kept verbatim on an `unhandled_submission` because that item type means
     * "nothing here could be filed automatically" — and the only way to handle
     * one by hand is to read what the person actually wrote.
     */
    payload?: Record<string, unknown> | null;
  };
}

type State<T> = { data: T[]; loading: boolean; error: string | null };

function useQ<T>(build: () => Query<DocumentData>, deps: unknown[]): State<T> {
  const [state, set] = useState<State<T>>({ data: [], loading: true, error: null });
  useEffect(() => {
    set((s) => ({ ...s, loading: true }));
    return onSnapshot(
      build(),
      (snap) =>
        set({
          data: snap.docs.map((d) => ({ id: d.id, ...d.data() }) as T),
          loading: false,
          error: null,
        }),
      (err) => set({ data: [], loading: false, error: err.message }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
}

/**
 * Today's list for one owner.
 *
 * Scoped to `dayKey` rather than to "everything not yet handled". That is what
 * spec §4.1 means by no backlog: a week away does not produce a pile, because
 * yesterday's untouched cards are simply not today's list. Anything that still
 * matters is re-proposed by this morning's run and re-ranked against today.
 *
 * `in_progress` is included alongside `ready`: tapping WhatsApp opens a chat and
 * marks the card started, and it has to stay on screen until somebody says the
 * conversation actually happened.
 */
export function useRecommendations(ownerSlug: string | null) {
  const day = todayKey();

  return useQ<Recommendation>(
    () => {
      const filters = [
        where("workspaceId", "==", WORKSPACE_ID),
        where("dayKey", "==", day),
        where("status", "in", ["ready", "in_progress"]),
      ];
      return query(
        collection(db, "recommendations"),
        ...(ownerSlug ? [...filters, where("assignedTo", "==", ownerSlug)] : filters),
        orderBy("rank", "asc"),
        // Two owners, each up to seven plus the "עוד משימות" extension (20).
        fbLimit(60),
      );
    },
    [day, ownerSlug],
  );
}

/** What was decided about the rest of today's cards. Shown behind a tap, so the
 *  day's work is reviewable without cluttering the list of what is left. */
export function useHandledRecommendations(ownerSlug: string | null) {
  const day = todayKey();

  return useQ<Recommendation>(
    () => {
      const filters = [
        where("workspaceId", "==", WORKSPACE_ID),
        where("dayKey", "==", day),
        where("status", "in", ["done", "snoozed", "dismissed", "reassigned"]),
      ];
      return query(
        collection(db, "recommendations"),
        ...(ownerSlug ? [...filters, where("assignedTo", "==", ownerSlug)] : filters),
        fbLimit(40),
      );
    },
    [day, ownerSlug],
  );
}

/** Page size for the Inbox. Small enough to render fast, and never silent. */
export const INBOX_PAGE_SIZE = 40;

const INBOX_NON_CONTACT_TYPES = [
  "unattributed_payment",
  "possible_duplicate",
  "unhandled_submission",
  "new_contact",
  "registration",
] as const;

export function useInbox(pageSize = INBOX_PAGE_SIZE) {
  return useQ<InboxItem>(
    () =>
      query(
        collection(db, "inbox_items"),
        where("workspaceId", "==", WORKSPACE_ID),
        where("status", "==", "open"),
        where("type", "in", [...INBOX_NON_CONTACT_TYPES]),
        fbLimit(pageSize),
      ),
    [pageSize],
  );
}

/**
 * How many open non-contact inbox items there really are.
 *
 * Google Contacts (`contact_to_triage`) live exclusively on `/triage` so the
 * main Inbox badge reflects only real incoming payments, identity conflicts,
 * and unhandled form submissions.
 */
export function useInboxTotal(): number | null {
  const [total, setTotal] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    getCountFromServer(
      query(
        collection(db, "inbox_items"),
        where("workspaceId", "==", WORKSPACE_ID),
        where("status", "==", "open"),
        where("type", "in", [...INBOX_NON_CONTACT_TYPES]),
      ),
    )
      .then((snap) => {
        if (!cancelled) setTotal(snap.data().count);
      })
      .catch(() => {
        if (!cancelled) setTotal(null);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return total;
}

export function useContactsToTriageTotal(): number | null {
  const [total, setTotal] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    getCountFromServer(
      query(
        collection(db, "inbox_items"),
        where("workspaceId", "==", WORKSPACE_ID),
        where("status", "==", "open"),
        where("type", "==", "contact_to_triage"),
      ),
    )
      .then((snap) => {
        if (!cancelled) setTotal(snap.data().count);
      })
      .catch(() => {
        if (!cancelled) setTotal(null);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return total;
}

/**
 * The capture box — a Trello-style inbox for things you do not want to forget.
 *
 * ## Why it is a collection of its own
 *
 * `inbox_items` is a queue of decisions **the system** refused to make: an
 * unmatched payment, a possible duplicate, a contact from Google that might be a
 * neighbour or might be a plumber from 2019. Production holds 449 of them and
 * every one was put there by a machine. The product owner asked for something
 * else entirely, and said so precisely: "זה צריך להיות כמו שיש אינבוקס בטרלו,
 * שכותבים שם דברים כדי שלא ישכחו ואחר כך ממנינים אותם למקומם."
 *
 * Mixing the two would have wrecked both. A note somebody typed to themselves
 * has no source, no evidence and no suggested action, and cannot be resolved by
 * `resolveInboxItem` — while every server rule, watchdog and audit path that
 * reads `inbox_items` assumes it is looking at a decision with a document behind
 * it. So capture lives in `captures`, the two are shown on the same screen, and
 * the Inbox finally holds both kinds of thing that belong in an inbox.
 *
 * Open notes only, newest first — which is the order they were written in, and
 * the order a scratchpad is read in.
 *
 * Ordered by `createdAtLocal` rather than `createdAt`. A `serverTimestamp()`
 * reads as `null` from the local cache until the server acknowledges the write,
 * so a list ordered by it descending puts the note just typed at the bottom —
 * indistinguishable from the note not saving. See `captureNote`.
 */
export interface Capture extends DocumentData {
  id: string;
  text?: string;
  status?: "open" | "filed" | "done" | "deleted";
  createdByUid?: string;
  /** Client clock, and what the list orders by — see `captureNote`. */
  createdAtLocal?: string;
  createdAt?: Timestamp;
  filedAs?: { type: string; id: string } | null;
}

export function useCaptures(pageSize = 50) {
  return useQ<Capture>(
    () =>
      query(
        collection(db, "captures"),
        where("workspaceId", "==", WORKSPACE_ID),
        where("status", "==", "open"),
        orderBy("createdAtLocal", "desc"),
        fbLimit(pageSize),
      ),
    [pageSize],
  );
}

/**
 * Contacts awaiting triage, newest first.
 *
 * Ordered by `sortKey` — the date Google last touched the contact — because with
 * hundreds of cards the order is what makes the queue tractable. Somebody added
 * last month is far more likely to be a neighbour than somebody whose record was
 * last edited three years ago.
 */
export function useContactsToTriage(pageSize = INBOX_PAGE_SIZE) {
  return useQ<InboxItem>(
    () =>
      query(
        collection(db, "inbox_items"),
        where("workspaceId", "==", WORKSPACE_ID),
        where("status", "==", "open"),
        where("type", "==", "contact_to_triage"),
        orderBy("sortKey", "desc"),
        fbLimit(pageSize),
      ),
    [pageSize],
  );
}

/**
 * Three vocabularies, distinguished by `kind`:
 *
 *   - `person`            — what somebody is to the Chabad house (תגיות)
 *   - `business_service`  — what a business in the neighbourhood provides
 *   - `organization_type` — what kind of institution an organization is
 *
 * `business_service` was created by the historical import — 33 entries, from
 * "שיפוצים" to "ציפורניים ופדיקור" — and then had no screen at all, because the
 * only two kinds this file knew about were the other two. A vocabulary nobody
 * can see is a vocabulary nobody can fix.
 */
export type TagKind = "person" | "business_service" | "organization_type";

/**
 * Work that arrived through a form.
 *
 * The Inbox had a tab called "טפסים והרשמות" that could never contain anything.
 * It filtered `inbox_items` for `type == "registration"`, and the processor
 * never creates one: a registration goes to `registrations`, an enquiry becomes
 * an `interaction` plus a `task`, and a mezuzah request becomes a
 * `service_request`. All three were being written correctly and none of them had
 * a screen, so a form submitted on the website genuinely did land nowhere the
 * product owner could see it.
 *
 * The distinction the Inbox was built on is still right — `inbox_items` is for
 * decisions the system refuses to guess, not for work. So this reads the work
 * from where the work actually lives, and the two are shown side by side rather
 * than mixed into one collection.
 */
export interface WorkItem {
  id: string;
  kind: "registration" | "service_request" | "task";
  title: string;
  lines: string[];
  personId: string | null;
  personName?: string | null;
  personPhone?: string | null;
  /** ISO instant, for ordering across the three sources. */
  at: string | null;
  /** Set on a task whose reply is already late. */
  overdue: boolean;
  /** On a task: which rule opened it, so a screen can leave out another screen's work. */
  sourceRuleId?: string | null;
}

function isoOf(v: unknown): string | null {
  if (typeof v === "string") return v;
  if (v && typeof v === "object" && "toDate" in v) {
    const d = (v as Timestamp).toDate();
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
  return null;
}

/** Enquiries from the website still owed a reply. */
/**
 * Open tasks, soonest first.
 *
 * `excludeRules` leaves out tasks a screen does not show, and is applied
 * **before** the page is cut, not after. The Inbox used to load forty tasks and
 * then drop the thank-yous, and with 79 thank-yous due at once there was nothing
 * left — the enquiries it exists to show were past the fortieth row (ADR-036).
 */
export function useOpenTasks(pageSize = 40, excludeRules: string[] = []) {
  const excludeKey = excludeRules.join(",");
  const fetchSize = Math.min(pageSize + 300, 500);
  const state = useQ<DocumentData>(
    () =>
      query(
        collection(db, "tasks"),
        where("workspaceId", "==", WORKSPACE_ID),
        where("status", "==", "open"),
        orderBy("dueAt", "asc"),
        fbLimit(fetchSize),
      ),
    [fetchSize],
  );

  const now = Date.now();
  const excluded = new Set(excludeKey === "" ? [] : excludeKey.split(","));
  // Inbox tasks are strictly website enquiries (`intake.contact_request.reply`).
  // Personal follow-up reminders live on Today and TasksPage (`useFollowUps`).
  const kept = state.data
    .filter((t) => {
      const rule = typeof t.sourceRuleId === "string" ? t.sourceRuleId : "";
      if (excluded.has(rule) || rule === "intake.thank") return false;
      return rule === "intake.contact_request.reply";
    })
    .slice(0, pageSize);
  return {
    ...state,
    items: kept.map<WorkItem>((t) => {
      const due = isoOf(t.dueAt);
      return {
        id: String(t.id),
        kind: "task",
        title: String(t.title ?? "משימה"),
        lines: [String(t.description ?? "")].filter((s) => s !== ""),
        personId: typeof t.personId === "string" ? t.personId : null,
        at: isoOf(t.createdAt) ?? due,
        overdue: due != null && new Date(due).getTime() < now,
        sourceRuleId: typeof t.sourceRuleId === "string" ? t.sourceRuleId : null,
      };
    }),
  };
}

export interface FollowUp {
  id: string;
  title: string;
  description: string;
  personId: string | null;
  dueAt: string | null;
  overdue: boolean;
  /** How many times it has been pushed back. Five is a decision nobody said aloud. */
  snoozeCount: number;
  /** Null means a person wrote it; anything else means a rule proposed it. */
  sourceRuleId: string | null;
}

/**
 * Every open personal follow-up reminder, soonest first.
 *
 * Excludes `intake.thank` and `intake.contact_request.reply` (which live in the Inbox).
 */
export function useFollowUps(limitTo = 200) {
  const state = useQ<DocumentData>(
    () =>
      query(
        collection(db, "tasks"),
        where("workspaceId", "==", WORKSPACE_ID),
        where("status", "==", "open"),
        orderBy("dueAt", "asc"),
        fbLimit(limitTo),
      ),
    [limitTo],
  );

  const now = Date.now();
  return {
    ...state,
    items: state.data
      .filter((t) => {
        const rule = typeof t.sourceRuleId === "string" ? t.sourceRuleId : "";
        return rule !== "intake.thank" && rule !== "intake.contact_request.reply";
      })
      .map<FollowUp>((t) => {
        const due = isoOf(t.dueAt);
        return {
          id: String(t.id),
          title: String(t.title ?? "משימה"),
          description: String(t.description ?? ""),
          personId: typeof t.personId === "string" ? t.personId : null,
          dueAt: due,
          overdue: due != null && new Date(due).getTime() < now,
          snoozeCount: typeof t.snoozeCount === "number" ? t.snoozeCount : 0,
          sourceRuleId: typeof t.sourceRuleId === "string" ? t.sourceRuleId : null,
        };
      }),
  };
}

export function useRecentRegistrations(pageSize = 40) {
  const state = useQ<DocumentData>(
    () =>
      query(
        collection(db, "registrations"),
        where("workspaceId", "==", WORKSPACE_ID),
        where("status", "==", "submitted"),
        orderBy("createdAt", "desc"),
        fbLimit(pageSize),
      ),
    [pageSize],
  );

  return {
    ...state,
    items: state.data.map<WorkItem>((r) => {
      const g = (r.guestCounts ?? {}) as { adults?: number; children?: number };
      const total = (g.adults ?? 0) + (g.children ?? 0);
      return {
        id: String(r.id),
        kind: "registration",
        // Never the raw id. That field used to hold a page slug, which at least
        // read as words; it now holds a document reference, and a card saying
        // "הרשמה · evt_8d20f8bde7ba…" is worse than one that just says what it is.
        title: typeof r.eventSlug === "string" && r.eventSlug !== ""
          ? `הרשמה · ${r.eventSlug}`
          : "הרשמה לאירוע",
        lines: [
          total > 0 ? `${g.adults ?? 0} מבוגרים · ${g.children ?? 0} ילדים` : "",
          String((r.answers as { notes?: string } | undefined)?.notes ?? ""),
        ].filter((s) => s !== ""),
        personId: typeof r.primaryPersonId === "string" ? r.primaryPersonId : null,
        at: isoOf(r.createdAt),
        overdue: false,
      };
    }),
  };
}

export function useOpenServiceRequests(pageSize = 40) {
  const state = useQ<DocumentData>(
    () =>
      query(
        collection(db, "service_requests"),
        where("workspaceId", "==", WORKSPACE_ID),
        where("status", "in", ["new", "triaged", "scheduled"]),
        orderBy("createdAt", "desc"),
        fbLimit(pageSize),
      ),
    [pageSize],
  );

  /**
   * Only a fallback now. The processor writes a `label` — "בר מצווה · הכנה
   * לעלייה לתורה ותפילין" — and this table was being used *instead* of it, so a
   * request whose stored title said exactly what it was still displayed as the
   * generic name of its family. Every card in the Inbox read "בקשת שירות".
   */
  const LABEL: Record<string, string> = {
    mezuzah: "בדיקת מזוזה",
    bar_mitzvah: "בר מצווה",
    rabbi_visit: "ביקור של הרב",
    class_join: "הצטרפות לשיעור",
    maternity_help: "שפרה ופועה · יולדת",
    tzedakah_box: "קופת צדקה",
    equipment_loan: 'גמ"ח ציוד',
    memorial: "הנצחה / יארצייט",
    matzah: "מצה שמורה",
    chametz_sale: "מכירת חמץ",
    other: "בקשת שירות",
  };

  /** The words the person actually filled in, whichever field carried them. */
  const requested = (s: DocumentData): string => {
    const d = (s.details ?? {}) as { serviceType?: unknown; track?: unknown };
    for (const v of [d.serviceType, d.track]) if (typeof v === "string" && v.trim()) return v.trim();
    return "";
  };

  return {
    ...state,
    items: state.data.map<WorkItem>((s) => ({
      id: String(s.id),
      kind: "service_request",
      // The stored label leads: it is the one string built from what the form
      // actually said. The family name is the fallback for records written
      // before the processor produced one.
      title:
        (typeof s.label === "string" && s.label.trim()) ||
        LABEL[String(s.type)] ||
        "בקשת שירות",
      lines: [
        // Shown when the title did not already say it, so a card is never just
        // the same three words twice.
        typeof s.label === "string" && s.label.includes(requested(s)) ? "" : requested(s),
        String(s.address ?? ""),
        String(s.notes ?? ""),
      ].filter((x) => x !== "" && x !== "undefined"),
      personId: typeof s.personId === "string" ? s.personId : null,
      at: isoOf(s.createdAt),
      overdue: false,
    })),
  };
}

export interface TagDef extends DocumentData {
  id: string;
  kind?: TagKind;
  name?: string;
  status?: "active" | "hidden";
  sortOrder?: number;
  /** `historical_import` for the ones nobody chose deliberately. */
  source?: string;
  /** How many people carry it, refreshed nightly. Orders the pickers. */
  usageCount?: number;
}

export function useTagDefs(kind: TagKind) {
  return useQ<TagDef>(
    () =>
      query(
        collection(db, "tag_defs"),
        where("workspaceId", "==", WORKSPACE_ID),
        where("kind", "==", kind),
        fbLimit(200),
      ),
    [kind],
  );
}

/**
 * How many people the full list loads at a time.
 *
 * Was 100. The product owner asked why they only ever see a hundred, and the
 * answer was that the list was built for browsing and they were using it for
 * clearing out — 3,933 people at a hundred a tap is forty taps before you have
 * even seen everybody. A Firestore page of 500 person documents is a few hundred
 * kilobytes; the hundred was caution, not a constraint.
 */
export const PEOPLE_PAGE_SIZE = 500;

export function usePeople(pageSize = PEOPLE_PAGE_SIZE) {
  return useQ<Person>(
    () =>
      query(
        collection(db, "people"),
        where("workspaceId", "==", WORKSPACE_ID),
        where("status", "==", "active"),
        orderBy("normalizedName", "asc"),
        fbLimit(pageSize),
      ),
    [pageSize],
  );
}

/**
 * How many people there really are.
 *
 * The list is capped so the screen renders, and for a while that cap was the
 * number on screen: after importing 3,893 people the page showed 300 and said
 * nothing about the rest, which reads as "the import only brought 300". The same
 * mistake the Inbox made with its 60.
 */
export function usePeopleTotal(): number | null {
  const [total, setTotal] = useState<number | null>(null);

  /**
   * Recounted when the underlying list changes, not only on mount.
   *
   * A count aggregation is a one-shot read, so creating somebody left the header
   * saying 3,892 until the screen was navigated away from and back. The live
   * query below costs nothing extra — the People screen is already subscribed to
   * it — and its size changing is the signal that the total moved.
   */
  const [tick, setTick] = useState(0);
  useEffect(() => {
    return onSnapshot(
      query(
        collection(db, "people"),
        where("workspaceId", "==", WORKSPACE_ID),
        where("status", "==", "active"),
        fbLimit(1),
      ),
      (snap) => {
        // Any write to the collection nudges this; the metadata check keeps a
        // local echo from causing a second server round trip.
        if (!snap.metadata.hasPendingWrites) setTick((n) => n + 1);
      },
      () => {},
    );
  }, []);

  useEffect(() => {
    let cancelled = false;
    getCountFromServer(
      query(
        collection(db, "people"),
        where("workspaceId", "==", WORKSPACE_ID),
        where("status", "==", "active"),
      ),
    )
      .then((s) => !cancelled && setTotal(s.data().count))
      .catch(() => !cancelled && setTotal(null));
    return () => {
      cancelled = true;
    };
  }, [tick]);

  return total;
}

/**
 * Search across **all** people, not just the loaded page.
 *
 * Firestore cannot do substring search, so this is a prefix match on
 * `normalizedName` — the standard range trick, where `` is above any
 * ordinary character and so bounds the prefix. Client-side filtering of a capped
 * page was worse than it looked: with 3,893 people it silently searched the
 * first hundred and reported "not found" for somebody who was right there.
 *
 * Returns null when the term is too short to be worth a query.
 */
/**
 * Finding a person by any part of their name, not just the start of it.
 *
 * This was a prefix range over `normalizedName`:
 *
 * ```
 * where("normalizedName", ">=", q), where("normalizedName", "<=", q + "\uf8ff")
 * ```
 *
 * which matches only names *beginning* with what was typed. "\u05d9\u05d5\u05d1\u05dc \u05d7\u05d1\u05d0\u05d6" came up
 * for "\u05d9\u05d5\u05d1\u05dc" and not for "\u05d7\u05d1\u05d0\u05d6" \u2014 reported in exactly those words, and it is the
 * wrong half: in a list of 3,894 people the surname is the selective one.
 *
 * Firestore cannot search inside a string, so the substrings are stored instead.
 * `searchTokens` holds every prefix of every word of the name, plus phone digits
 * and the email name, maintained by a trigger on the collection. The query is a
 * single `array-contains` \u2014 one index lookup, no scan, and the same cost at
 * 40,000 people as at 4,000.
 *
 * **One token is queried, the rest are applied here.** `array-contains-any` is
 * an OR, so a second word would *widen* the search when typing more is how a
 * person narrows it. The longest word goes to Firestore because it is the most
 * selective; the remaining words are checked against the fifty rows that come
 * back. `matchesAllWords` is the same function the tokens were built with, so
 * the client and the index cannot disagree about what a word is.
 */
export function usePeopleSearch(term: string) {
  const token = searchToken(term);

  const state = useQ<Person>(
    () =>
      query(
        collection(db, "people"),
        where("workspaceId", "==", WORKSPACE_ID),
        where("searchTokens", "array-contains", token ?? "__none__"),
        fbLimit(50),
      ),
    [token],
  );

  if (!token) return { data: [], loading: false, error: null };

  return {
    ...state,
    data: state.data
      .filter((p) => matchesAllWords((p.searchTokens as string[] | undefined) ?? [], term))
      .sort((a, b) => (a.displayName ?? "").localeCompare(b.displayName ?? "", "he")),
  };
}

/**
 * Searching the **timeline** by subject, across everybody at once.
 *
 * ## The request, in the product owner's words
 *
 * "אם אני כותב שיעור גמרא, זה אמור להביא לי את כל מי שהיה בשיעור גמרא בציר הזמן
 * וכדו'" — and the "וכדו'" is the important part. The subject of a timeline entry
 * is frequently the only handle anybody has on it: not who came to "מכירת חמץ",
 * but that there was a מכירת חמץ and a list of people attached to it. Production
 * holds 3,445 such entries — attendance, donations, calls, enquiries — with
 * subjects like 'רישום גן חב"ד תשפ"ו', and until now not one of them was
 * reachable by any query. Search knew only about names.
 *
 * ## One collection-group query, not 3,933 reads
 *
 * `activities` is a subcollection per person, so the naive shape is to read
 * every person's timeline and filter. A collection-group query asks the same
 * question once, against an index, and costs the same at forty thousand people
 * as at four thousand. Two things have to exist for it to work at all, and both
 * were added with this hook: a `COLLECTION_GROUP` composite index, because a
 * subcollection's fields are not group-indexed by default, and a Security Rule
 * on a wildcard path, because the nested rule does not authorise a group query.
 *
 * ## Grouped by subject, because the rows are not the answer
 *
 * Eighty attendance entries for one class is eighty rows of the same four words.
 * The question behind the search is "who was at it", so the results collapse to
 * one line per subject carrying the people underneath — which is also the thing
 * the product owner asked for separately: "איך אני מגיע לכל הנרשמים למשהו
 * ספציפי".
 *
 * Titles are grouped exactly as written. "תרומות" and "תרומה כללית" are two
 * different subjects in the source data and merging them would invent a total
 * nobody recorded.
 */
/**
 * Six hundred, measured rather than guessed.
 *
 * The largest single subject in production is מכירת חמץ at 421 entries, and the
 * next is 182. A cap below the biggest real group would truncate the one search
 * most worth running, and one far above it would pull a megabyte over a phone
 * connection for every search that matches a common word.
 */
export const SUBJECT_SEARCH_LIMIT = 600;

export interface SubjectHit {
  /** The subject line, exactly as the timeline holds it. */
  title: string;
  /** `attendance`, `donation`, `call`… the most common kind under this subject. */
  kind: string;
  /** Everybody with an entry under this subject. */
  personIds: string[];
  /** The most recent occurrence, ISO, or null where the import knew no date. */
  lastAt: string | null;
}

export function useSubjectSearch(term: string): {
  subjects: SubjectHit[];
  loading: boolean;
  error: string | null;
  /** True when the query filled its cap, so there may be more than is shown. */
  truncated: boolean;
} {
  const token = searchToken(term);
  const [rows, setRows] = useState<
    { personId: string; title: string; kind: string; at: string | null; tokens: string[] }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);

  useEffect(() => {
    if (!token) {
      setRows([]);
      setLoading(false);
      setError(null);
      setTruncated(false);
      return;
    }
    setLoading(true);
    setError(null);
    return onSnapshot(
      query(
        collectionGroup(db, "activities"),
        where("workspaceId", "==", WORKSPACE_ID),
        where("searchTokens", "array-contains", token),
        orderBy("occurredAt", "desc"),
        fbLimit(SUBJECT_SEARCH_LIMIT),
      ),
      (snap) => {
        setRows(
          snap.docs
            .map((d) => ({
              // The person is the grandparent document. This is the whole reason
              // a group query is worth it: one read yields both the match and
              // whose timeline it is on.
              personId: d.ref.parent.parent?.id ?? "",
              title: String(d.get("title") ?? "").trim(),
              kind: String(d.get("activityType") ?? "other"),
              at: typeof d.get("occurredAt") === "string" ? (d.get("occurredAt") as string) : null,
              tokens: (d.get("searchTokens") as string[] | undefined) ?? [],
            }))
            .filter((r) => r.personId !== "" && r.title !== ""),
        );
        setTruncated(snap.size >= SUBJECT_SEARCH_LIMIT);
        setLoading(false);
        setError(null);
      },
      (err) => {
        // Surfaced, never swallowed. An empty list here would read as "nobody
        // ever went to that" — which is exactly what a missing index or a denied
        // read would look like, and both have happened on this project.
        setRows([]);
        setLoading(false);
        setTruncated(false);
        setError(err.message);
      },
    );
  }, [token]);

  const subjects = useMemo(() => {
    const byTitle = new Map<string, { kinds: Map<string, number>; people: Set<string>; lastAt: string | null }>();
    for (const r of rows) {
      // Every word typed has to be satisfied, not only the one word that went to
      // the index — the same narrowing the people search does, with the same
      // function the tokens were built by, so the client and the index cannot
      // disagree about what a word is.
      //
      // Applied here rather than in the snapshot handler on purpose: the query
      // is keyed on the token, and re-running it for each extra letter would be
      // a fresh set of billable reads and a loading flash per keystroke.
      if (!matchesAllWords(r.tokens, term)) continue;
      let g = byTitle.get(r.title);
      if (!g) {
        g = { kinds: new Map(), people: new Set(), lastAt: null };
        byTitle.set(r.title, g);
      }
      g.kinds.set(r.kind, (g.kinds.get(r.kind) ?? 0) + 1);
      g.people.add(r.personId);
      if (r.at && (g.lastAt === null || r.at > g.lastAt)) g.lastAt = r.at;
    }

    return [...byTitle.entries()]
      .map(([title, g]) => ({
        title,
        kind: [...g.kinds.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? "other",
        personIds: [...g.people],
        lastAt: g.lastAt,
      }))
      // Most people first: a subject forty people share is more likely to be the
      // one being looked for than a note on one person's card.
      .sort((a, b) => b.personIds.length - a.personIds.length || a.title.localeCompare(b.title, "he"));
  }, [rows, term]);

  return { subjects, loading, error, truncated };
}

/**
 * Events matching what was typed.
 *
 * Filtered on the client, and that is the right call here rather than a
 * concession: there are 32 events in production, the whole collection is one
 * cheap read, and an event's registrations are reached through its own screen.
 * Tokenising 32 documents to save a filter would be machinery in place of an
 * `if`.
 *
 * The tokens are built with the same function the people index uses, so "שיעור
 * גמרא" matches "שיעור גמרא -ברכות" by the same rules that make "חבאז" match
 * "יובל חבאז".
 */
export function useEventSearch(term: string) {
  const active = searchToken(term) !== null;
  const all = useQ<CrmEvent>(
    () =>
      query(
        collection(db, "events"),
        where("workspaceId", "==", WORKSPACE_ID),
        fbLimit(active ? 300 : 1),
      ),
    [active],
  );

  const matches = useMemo(() => {
    if (!active) return [];
    return all.data
      .filter((e) =>
        matchesAllWords(
          buildSearchTokens({ displayName: e.title ?? "", extra: [e.location?.name ?? null] }),
          term,
        ),
      )
      .sort((a, b) => (b.startDate ?? "").localeCompare(a.startDate ?? ""));
  }, [all.data, active, term]);

  return { data: matches, loading: active ? all.loading : false, error: active ? all.error : null };
}

/** Everybody carrying one tag. Queried on demand rather than counted up front. */
export function usePeopleByTag(tagId: string | null) {
  const state = useQ<Person>(
    () =>
      query(
        collection(db, "people"),
        where("workspaceId", "==", WORKSPACE_ID),
        where("tagIds", "array-contains", tagId ?? "__none__"),
        fbLimit(200),
      ),
    [tagId],
  );

  return tagId ? state : { data: [], loading: false, error: null };
}

/**
 * Everybody carrying no tag at all.
 *
 * 2,190 of 3,933 people, and until now unreachable: Firestore cannot filter on
 * the length of an array, and `tagIds == []` would miss everyone whose field is
 * absent rather than empty. `tagCount` is written by the trigger on `people`,
 * so it cannot drift out of step with the tags themselves.
 *
 * The product owner asked for "a tag for whoever has no tag". A real tag would
 * be wrong — it would have to be added and removed on every change, and would be
 * stale within a day. This answers the same question and cannot go stale.
 *
 * Loaded in large pages on purpose. This list exists to be *cleared*, and
 * clearing it a hundred at a time is why it never got cleared.
 */
export const UNTAGGED_PAGE_SIZE = 500;

export function usePeopleWithoutTags(pageSize = UNTAGGED_PAGE_SIZE) {
  /**
   * Never zero, and this is not defensive tidying — it broke the screen.
   *
   * The caller passes `0` to mean "not looking at this list", and Firestore's
   * `limit()` **throws** on a non-positive number rather than returning nothing.
   * The exception came out of the subscription effect, React unmounted the tree,
   * and the whole People screen went white and stuck. Reported within minutes of
   * the deploy, which is exactly what it deserved.
   *
   * A disabled hook still has to be a hook — it cannot be skipped without
   * changing hook order — so it subscribes to one document and the result is
   * discarded below. That is the same shape `usePeopleByTag` already uses for
   * the same reason.
   */
  const size = Math.max(1, pageSize);

  const state = useQ<Person>(
    () =>
      query(
        collection(db, "people"),
        where("workspaceId", "==", WORKSPACE_ID),
        where("tagCount", "==", 0),
        orderBy("normalizedName", "asc"),
        fbLimit(size),
      ),
    [size],
  );

  return pageSize > 0 ? state : { data: [], loading: false, error: null };
}

/** How many carry no tag, without loading them. */
export function useUntaggedTotal(): number | null {
  const [total, setTotal] = useState<number | null>(null);
  useEffect(() => {
    getCountFromServer(
      query(
        collection(db, "people"),
        where("workspaceId", "==", WORKSPACE_ID),
        where("tagCount", "==", 0),
      ),
    )
      .then((s) => setTotal(s.data().count))
      .catch(() => setTotal(null));
  }, []);
  return total;
}

/**
 * Who gives the most, in order, optionally within one tag.
 *
 * Every person already carried `donationTotalVerifiedMinor`,
 * `donationTotalImportedMinor` and `donationCount`, rebuilt nightly — and no
 * screen ordered by any of them. The product owner asked how to find the largest
 * donors and the answer was that you could not: "what do I do with buried data I
 * can't sort by?"
 *
 * Ordered by `donationTotalAllMinor`, the stored sum of both. Ranking needs one
 * number, Firestore cannot order by an expression, and keeping them separate for
 * the ranking would have meant two lists that disagree about who is first. The
 * person's own screen still shows the split, so nothing is hidden — the total is
 * for ordering, not for a receipt.
 *
 * `tagId` narrows it to one classification, because "the biggest donors" and
 * "the biggest donors among the שכונה families" are different questions and only
 * the second one is usually actionable.
 */
export function useTopDonors(tagId: string | null, pageSize = 50) {
  const state = useQ<Person>(
    () =>
      query(
        collection(db, "people"),
        where("workspaceId", "==", WORKSPACE_ID),
        ...(tagId ? [where("tagIds", "array-contains", tagId)] : []),
        where("donationTotalAllMinor", ">", 0),
        orderBy("donationTotalAllMinor", "desc"),
        fbLimit(pageSize),
      ),
    [tagId, pageSize],
  );

  return state;
}

/**
 * Donations that belong to nobody.
 *
 * Production holds 24 of them, worth ₪9,191: eighteen carrying
 * `unlinked_at_import` — a spreadsheet row naming somebody the importer could
 * not resolve — and six carrying `donor_person_deleted`, where the donor's
 * record was deleted afterwards and took the link with it.
 *
 * Until this hook there was no way to see any of them. They are not on a person,
 * so no person screen shows them; they are not in `inbox_items`, because the
 * processor raises `unattributed_payment` only for live payments and these
 * predate it; and the donors ranking lists people, so money attached to nobody
 * cannot appear there at all. It was ₪9,191 that nothing in the product could
 * reach.
 *
 * Newest first, because a name is most likely to still be remembered for the
 * most recent gift.
 */
export interface UnattributedDonation extends DocumentData {
  id: string;
  amountMinor?: number;
  purpose?: string | null;
  collectedAt?: string | null;
  provider?: string | null;
  verified?: boolean;
  reviewReasons?: string[];
}

export function useUnattributedDonations(pageSize = 50) {
  return useQ<UnattributedDonation>(
    () =>
      query(
        collection(db, "donations"),
        where("workspaceId", "==", WORKSPACE_ID),
        where("personId", "==", null),
        orderBy("collectedAt", "desc"),
        fbLimit(pageSize),
      ),
    [pageSize],
  );
}

/** Total giving in shekels — the figure the ranking uses. */
export function givingTotal(p: Person): number {
  const all = p.donationTotalAllMinor;
  if (typeof all === "number") return all / 100;
  // Older records written before the sum was stored.
  return ((p.donationTotalVerifiedMinor ?? 0) + (p.donationTotalImportedMinor ?? 0)) / 100;
}

/**
 * One person.
 *
 * `error` is separate from "not found" on purpose. A failed read used to leave
 * `person` as null, which the screen renders as "הרשומה לא נמצאה" — a confident
 * statement about the data made on the strength of a permission error. It is the
 * same mistake that hid the timeline for a fortnight: a read that did not happen
 * is not evidence that there is nothing there.
 */
export function usePerson(personId: string | undefined) {
  const [person, setPerson] = useState<Person | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!personId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    return onSnapshot(
      doc(db, "people", personId),
      (snap) => {
        setPerson(snap.exists() ? ({ id: snap.id, ...snap.data() } as Person) : null);
        setLoading(false);
        setError(null);
      },
      (err) => {
        setLoading(false);
        setError(err.message);
      },
    );
  }, [personId]);

  return { person, loading, error };
}

export interface Household extends DocumentData {
  id: string;
  displayName?: string;
  fatherName?: string;
  motherName?: string;
  childrenNames?: string[];
  membersCount?: number;
  /** Resolved by `backfillFamilies`. `membersCount` is what the source claimed;
   *  this is how many people actually point at the household. */
  membersResolved?: number;
  /** `address_group` means the source grouped these people by street, not by
   *  family. Twenty-two people from five families in one of them. */
  kind?: "family" | "address_group";
  distinctSurnames?: number;
  memberIds?: string[];
  memberNames?: string[];
  primaryContactPersonId?: string;
  primaryPhone?: string;
  address?: Record<string, string>;
}

/**
 * The family a person belongs to.
 *
 * A single document read rather than a denormalised copy on the person: a
 * household's membership changes whenever anybody is added or deleted, and a
 * copy would be wrong until the next backfill. The person screen is one screen
 * about one person — one extra read is the right price for it being true.
 */
export function useHousehold(householdId: string | undefined) {
  const [household, setHousehold] = useState<Household | null>(null);
  const [loading, setLoading] = useState(Boolean(householdId));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!householdId) {
      setHousehold(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    return onSnapshot(
      doc(db, "households", householdId),
      (snap) => {
        setHousehold(snap.exists() ? ({ id: snap.id, ...snap.data() } as Household) : null);
        setLoading(false);
        setError(null);
      },
      (err) => {
        // Not swallowed into null: null renders as "not part of a household",
        // which would be a false claim about somebody's family drawn from a
        // failed read.
        setHousehold(null);
        setLoading(false);
        setError(err.message);
      },
    );
  }, [householdId]);

  return { household, loading, error };
}

/**
 * Named people by id, for the small fixed lists a screen needs — a household's
 * members, the people sharing a phone number. Firestore's `in` filter takes at
 * most 30 values, which is well above any real family and is the reason this can
 * be one query instead of a loop of document reads.
 */
export function usePeopleByIds(ids: string[] | undefined) {
  const key = (ids ?? []).slice(0, 30).sort().join(",");

  const state = useQ<Person>(
    () =>
      query(
        collection(db, "people"),
        where("workspaceId", "==", WORKSPACE_ID),
        where(documentId(), "in", key === "" ? ["__none__"] : key.split(",")),
      ),
    [key],
  );

  return key === "" ? { data: [], loading: false, error: null } : state;
}

/**
 * A named set of people, however many there are.
 *
 * `usePeopleByIds` caps at thirty because that is Firestore's limit on an `in`
 * clause, which is fine for the two or three candidates of a duplicate card and
 * wrong for the answer to "who was at מכירת חמץ" — fifty-seven people in
 * production, silently cut to thirty. So this one chunks, and says how many it
 * could not fetch rather than returning a short list as if it were complete.
 *
 * `getDocs` rather than a live subscription: this list is the result of a search
 * somebody just typed, and holding ten snapshot listeners open while they type
 * the next letter costs more than reading it again.
 */
const PEOPLE_BY_ID_CHUNK = 30;
/** Enough for the largest real group — מכירת חמץ, 421 people — so the answer to
 *  "who was at it" is the whole list rather than a round number of it. */
const PEOPLE_BY_ID_MAX = 500;

export function useNamedPeople(ids: string[]): {
  data: Person[];
  loading: boolean;
  error: string | null;
  /** How many ids were beyond the ceiling and never fetched. */
  omitted: number;
} {
  const key = [...new Set(ids)].sort().join(",");
  const [state, setState] = useState<{ data: Person[]; loading: boolean; error: string | null }>({
    data: [],
    loading: false,
    error: null,
  });

  useEffect(() => {
    if (key === "") {
      setState({ data: [], loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState({ data: [], loading: true, error: null });

    const wanted = key.split(",").slice(0, PEOPLE_BY_ID_MAX);
    const chunks: string[][] = [];
    for (let i = 0; i < wanted.length; i += PEOPLE_BY_ID_CHUNK) {
      chunks.push(wanted.slice(i, i + PEOPLE_BY_ID_CHUNK));
    }

    Promise.all(
      chunks.map((c) =>
        getDocs(
          query(
            collection(db, "people"),
            where("workspaceId", "==", WORKSPACE_ID),
            where(documentId(), "in", c),
          ),
        ),
      ),
    )
      .then((snaps) => {
        if (cancelled) return;
        const data = snaps
          .flatMap((s) => s.docs.map((d) => ({ id: d.id, ...d.data() }) as Person))
          .sort((a, b) => (a.displayName ?? "").localeCompare(b.displayName ?? "", "he"));
        setState({ data, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({ data: [], loading: false, error: err instanceof Error ? err.message : "לא הצלחנו לטעון" });
      });

    return () => {
      cancelled = true;
    };
  }, [key]);

  return { ...state, omitted: Math.max(0, new Set(ids).size - PEOPLE_BY_ID_MAX) };
}

export interface GoogleContactsMirror {
  /** Whether the same authorisation also granted calendar write access. */
  calendarEnabled?: boolean;
  status?: "active" | "revoked" | "disconnected" | "error";
  lastSyncAt?: Date | null;
  lastSyncStatus?: string | null;
  lastSyncError?: string | null;
  lastSyncCounts?: { scanned: number; offered: number; alreadyKnown: number; skipped: number } | null;
  connectedEmail?: string | null;
}

export interface MyProfile extends DocumentData {
  id: string;
  displayName?: string;
  email?: string;
  /**
   * Which of the two owners this account is.
   *
   * Recommendations are assigned by owner slug, not by uid: the import assigned
   * 3,893 people to `mendi` or `shira` long before either had an account, and
   * Shira may not have one yet. Without this the Today screen cannot tell whose
   * "שלי" it is showing, so the tab says so rather than guessing.
   */
  ownerSlug?: "mendi" | "shira" | null;
  googleContacts?: GoogleContactsMirror;
}

/**
 * The signed-in user's own profile document.
 *
 * This is where the Google connection *status* lives. The connection record
 * itself holds an encrypted refresh token and is closed to every client
 * including its owner, so the server mirrors the harmless fields here — where
 * the rules already let a user read their own document.
 */
export function useMyProfile(): MyProfile | null {
  const [profile, setProfile] = useState<MyProfile | null>(null);

  useEffect(() => {
    const uid = getAuth().currentUser?.uid;
    if (!uid) return;

    return onSnapshot(
      doc(db, "users", uid),
      (snap) => {
        if (!snap.exists()) {
          setProfile(null);
          return;
        }
        const data = snap.data();
        // The stored shape and the shape the UI wants differ in one field:
        // Firestore returns a Timestamp where the component wants a Date. Typing
        // the raw form separately keeps that conversion honest instead of
        // asserting one type is the other.
        const raw = data.googleContacts as
          | (Omit<GoogleContactsMirror, "lastSyncAt"> & { lastSyncAt?: Timestamp | null })
          | undefined;

        setProfile({
          id: snap.id,
          ...data,
          ...(raw
            ? {
                googleContacts: {
                  ...raw,
                  lastSyncAt: raw.lastSyncAt?.toDate?.() ?? null,
                },
              }
            : {}),
        } as MyProfile);
      },
      () => setProfile(null),
    );
  }, []);

  return profile;
}

/**
 * An upcoming personal date — a birthday, a yahrzeit, a bar mitzvah.
 *
 * The point of the screen these feed is being able to ask "who has a birthday
 * next month" **without already knowing who**. Until now the only way to see a
 * date was to open the person, which is the wrong way round: the whole value is
 * in the lookahead.
 */
export interface UpcomingDate {
  id: string;
  kind: "birthday" | "yahrzeit";
  /** ISO date, `YYYY-MM-DD`. */
  date: string;
  /** Who to contact. */
  personId: string | null;
  personName: string;
  /** For a yahrzeit: whose memory it is. */
  subjectName: string | null;
  hebrewLabel: string | null;
}

/**
 * Birthdays falling inside a date window.
 *
 * Ordered on `nextBirthdayAt`, which the KodeshBoard sync and the import both
 * compute and store. A Firestore ordered query silently excludes documents that
 * lack the field, which here is exactly right: a person with no known birthday
 * has nothing to show.
 */
export function useUpcomingBirthdays(fromIso: string, toIso: string) {
  return useQ<Person>(
    () =>
      query(
        collection(db, "people"),
        where("workspaceId", "==", WORKSPACE_ID),
        where("nextBirthdayAt", ">=", fromIso),
        where("nextBirthdayAt", "<=", toIso),
        orderBy("nextBirthdayAt", "asc"),
        fbLimit(200),
      ),
    [fromIso, toIso],
  );
}

export interface Memorial extends DocumentData {
  id: string;
  contactPersonId?: string;
  deceasedDisplayName?: string;
  nextOccurrenceAt?: string;
  dateOfDeathHebrew?: { dayRaw?: string | null; monthRaw?: string | null };
}

export function useUpcomingYahrzeits(fromIso: string, toIso: string) {
  return useQ<Memorial>(
    () =>
      query(
        collection(db, "memorials"),
        where("workspaceId", "==", WORKSPACE_ID),
        where("nextOccurrenceAt", ">=", fromIso),
        where("nextOccurrenceAt", "<=", toIso),
        orderBy("nextOccurrenceAt", "asc"),
        fbLimit(200),
      ),
    [fromIso, toIso],
  );
}

export interface TimelineEntry {
  id: string;
  /** `donation`, `attendance`, `call`, `whatsapp`… drives the icon and tint. */
  kind: string;
  title: string;
  subtitle: string | null;
  when: string;
  approximate: boolean;
  /** Other names the same event was recorded under by another source system. */
  alsoRecordedAs: string[];
  /** How many source records describe this one event. Above one means the entry
   *  is a merge of duplicates the export produced, not a repeated event. */
  sourceRecordCount: number;
  /**
   * The submission this entry came from, when it came from a form on the website.
   *
   * Already written on every intake-derived activity; the mapper simply dropped
   * it, which is why there was no way through to what the person filled in.
   */
  sourceEventId: string | null;
  /** An open follow-up left by this conversation. */
  followUpTaskId: string | null;
  followUpDueAt: string | null;
}

/**
 * The person's timeline (spec §4.5).
 *
 * ## The `workspaceId` filter is not optional
 *
 * This query used to omit it, and the whole timeline came back empty for
 * everybody — a man with fifteen donations and an attendance record looked like
 * somebody nobody had ever spoken to.
 *
 * The rule for this subcollection is `allow read: if
 * isViewerPlus(resource.data.workspaceId)`. For a **get** Firestore evaluates
 * that against the one document. For a **list** it cannot: it will not run a
 * query whose results it would have to inspect one by one to decide whether they
 * were allowed, so it requires the query itself to prove the condition. A query
 * that does not filter on `workspaceId` is rejected outright — the documents are
 * there and readable individually, and the list is denied.
 *
 * Every other query in this file filtered on `workspaceId` because the data is
 * workspace-scoped. This one did not, and so it was the only one that failed.
 *
 * ## Failures are surfaced, not swallowed
 *
 * The old error handler set the list to empty, which rendered as "no activity
 * yet" — indistinguishable from a person with nothing recorded, and the reason
 * this went unnoticed. A read that fails now says so.
 */
export function usePersonTimeline(personId: string | undefined): {
  entries: TimelineEntry[];
  loading: boolean;
  error: string | null;
} {
  const [rows, setRows] = useState<DocumentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!personId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    return onSnapshot(
      query(
        collection(db, "people", personId, "activities"),
        where("workspaceId", "==", WORKSPACE_ID),
        orderBy("occurredAt", "desc"),
        fbLimit(50),
      ),
      (snap) => {
        setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
        setError(null);
      },
      (err) => {
        setRows([]);
        setLoading(false);
        setError(err.message);
      },
    );
  }, [personId]);

  return {
    entries: rows.map((r) => ({
      id: String(r.id),
      kind: String(r.activityType ?? "other"),
      title: String(r.title ?? r.activityType ?? "פעילות"),
      subtitle: typeof r.summary === "string" && r.summary !== "" ? r.summary : null,
      when: activityWhen(r),
      // Carried through rather than hidden: "exact" and "a guess from a Hebrew
      // year" mean very different things on a timeline, and a reader deserves to
      // know which one they are looking at.
      approximate: r.datePrecision != null && r.datePrecision !== "exact",
      alsoRecordedAs: Array.isArray(r.alsoRecordedAs)
        ? (r.alsoRecordedAs as unknown[]).filter((x): x is string => typeof x === "string")
        : [],
      sourceRecordCount: typeof r.sourceRecordCount === "number" ? r.sourceRecordCount : 1,
      sourceEventId:
        r.sourceType === "intake" && typeof r.sourceEventId === "string" ? r.sourceEventId : null,
      followUpTaskId: typeof r.followUpTaskId === "string" ? r.followUpTaskId : null,
      followUpDueAt: typeof r.followUpDueAt === "string" ? r.followUpDueAt : null,
    })),
    loading,
    error,
  };
}

/** How the date reads on a timeline row. Imported records often have no date at
 *  all, and saying so is better than showing an empty line. */
function activityWhen(r: DocumentData): string {
  const raw = r.occurredAt;
  if (typeof raw !== "string" || raw === "") return "תאריך לא ידוע";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString("he-IL", { day: "numeric", month: "long", year: "numeric" });
}

export function primaryPhone(p: Person): string | null {
  const list = p.phones ?? [];
  return (list.find((x) => x.primary) ?? list[0])?.value ?? null;
}

export function primaryEmail(p: Person): string | null {
  const list = p.emails ?? [];
  return (list.find((x) => x.primary) ?? list[0])?.value ?? null;
}

/** Hebrew greeting matched to the hour, as in the Today mockup. */
export function greeting(d = new Date()): string {
  const h = d.getHours();
  if (h < 5) return "לילה טוב";
  if (h < 12) return "בוקר טוב";
  if (h < 17) return "צהריים טובים";
  if (h < 21) return "ערב טוב";
  return "לילה טוב";
}

/**
 * The Hebrew name of each kind of Inbox item.
 *
 * Trimmed to what the server can actually create, which is four things:
 * `contact_to_triage` (the Google address book), `possible_duplicate` (the
 * processor refusing to guess an identity), `unattributed_payment` (money with
 * no submission behind it) and `unhandled_submission` (a website form the CRM
 * does not know how to file). `new_contact` and `registration` are kept because
 * the demo data and older documents still carry them.
 *
 * Five entries were removed — `unmatched_payment`, `ambiguous_household`,
 * `missing_information`, `sync_error`, `processing_error`. Nothing writes any of
 * them, and `unmatched_payment` was the worst of the set: the real type is
 * `unattributed_payment`, so the one item on this screen that concerns money was
 * the one with no label at all. A vocabulary that lists types nobody creates and
 * misses the types that exist is worse than no vocabulary — it reads as coverage.
 */
export const INBOX_TYPE_LABEL: Record<string, string> = {
  contact_to_triage: "איש קשר מגוגל",
  possible_duplicate: "כפילות אפשרית",
  unattributed_payment: "תשלום ללא שיוך",
  unhandled_submission: "טופס שלא ידענו לשייך",
  new_contact: "איש קשר חדש",
  registration: "הרשמה חדשה",
};

/**
 * What to look at first.
 *
 * The Inbox was in document order, which in practice means whatever Firestore
 * handed back — and with hundreds of Google contacts in the same collection,
 * the one item about money could be on page four. These are ranked by what it
 * costs to leave them alone:
 *
 *  - **A payment with nobody attached** is money in the bank that no donor is
 *    credited with. It is also the only item here a receipt depends on.
 *  - **A submission the CRM could not file** is somebody who filled in a form on
 *    the website and is waiting for an answer nobody knows they are owed.
 *  - **A possible duplicate** is a judgement call that blocks the identity of
 *    two records; it gets worse as more data attaches to the wrong one.
 *  - **A contact from Google** costs nothing to postpone. There are hundreds and
 *    they are mostly plumbers.
 */
const INBOX_PRIORITY: Record<string, number> = {
  unattributed_payment: 0,
  unhandled_submission: 1,
  possible_duplicate: 2,
  registration: 3,
  new_contact: 4,
  contact_to_triage: 5,
};

export function inboxPriority(type?: string): number {
  // An unknown type sorts above the Google backlog rather than below it: a card
  // nobody has taught this screen about is more likely to be new and urgent than
  // to be the six-hundredth address-book entry.
  return INBOX_PRIORITY[type ?? ""] ?? 4;
}

/** Which Inbox tab an item belongs to. */
export function inboxBucket(t?: string): "contacts" | "forms" | "attention" {
  if (t === "new_contact" || t === "contact_to_triage") return "contacts";
  if (t === "registration") return "forms";
  // Everything unrecognised lands in "needs attention" rather than being
  // silently dropped from every tab — an item nobody sees is worse than an item
  // in the wrong tab.
  return "attention";
}

/**
 * An event.
 *
 * `startDate` is the plain Jerusalem calendar date alongside the timestamp, so a
 * "what is on today" query is an equality rather than timezone arithmetic at read
 * time. `hebrewLabel` is stored rather than computed here: §13 wants the Hebrew
 * date kept structurally, and one engine on the server beats every screen doing
 * its own conversion and disagreeing about sunset.
 */
export interface CrmEvent extends DocumentData {
  id: string;
  title?: string;
  /** The join key the website's registration forms post against. */
  slug?: string;
  description?: string;
  startAt?: Timestamp;
  endAt?: Timestamp | null;
  /** `YYYY-MM-DD`, Jerusalem. */
  startDate?: string;
  allDay?: boolean;
  hebrewLabel?: string;
  location?: { name?: string | null; address?: string | null } | null;
  status?: "draft" | "published" | "done" | "cancelled";
  cancelReason?: string | null;
  owners?: string[];
  capacity?: number | null;
  registrationOpen?: boolean;
  audienceTagIds?: string[];
  /** Denormalised, kept true by `refreshEventCounts`. Guests matter as much as
   *  registrations: 24 registrations and 71 people are different rooms. */
  counts?: { registered?: number; attendedPeople?: number; guests?: number };
  /** The budget unit the financial work will hang income and expenses from.
   *  Always null for now — the field exists so that work needs no migration. */
  campaignId?: string | null;
  /**
   * Series membership. A parent points at itself, so one query on `seriesId`
   * finds every occurrence including the first, with no special case.
   */
  seriesId?: string | null;
  isSeriesParent?: boolean;
  recurrenceText?: string | null;
  /** True once this occurrence was edited on its own. Series-wide edits skip it,
   *  so a week somebody moved to 18:30 does not get moved back. */
  detached?: boolean;
}

/** Events from today forward, soonest first. Cancelled ones are excluded from
 *  "what is coming up" but stay readable on their own page. */
export function useUpcomingEvents(limitTo = 40) {
  const [from] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });

  return useQ<CrmEvent>(
    () =>
      query(
        collection(db, "events"),
        where("workspaceId", "==", WORKSPACE_ID),
        where("startAt", ">=", from),
        orderBy("startAt", "asc"),
        fbLimit(limitTo),
      ),
    [from, limitTo],
  );
}

/** Events that already happened, most recent first — where "who came" lives. */
export function usePastEvents(limitTo = 30) {
  const [before] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });

  return useQ<CrmEvent>(
    () =>
      query(
        collection(db, "events"),
        where("workspaceId", "==", WORKSPACE_ID),
        where("startAt", "<", before),
        orderBy("startAt", "desc"),
        fbLimit(limitTo),
      ),
    [before, limitTo],
  );
}

/**
 * Events that can have attendance marked against them.
 *
 * Anything that has already started, most recent first — plus a few hours of
 * slack, because setting up early and ticking the people already in the room is
 * normal. Next month's Shabbat dinner is deliberately not here: recording who
 * came to something that has not happened is a fiction, and the server refuses
 * it anyway.
 */
export function useMarkableEvents(limitTo = 40) {
  const [until] = useState(() => new Date(Date.now() + 12 * 3600 * 1000));

  return useQ<CrmEvent>(
    () =>
      query(
        collection(db, "events"),
        where("workspaceId", "==", WORKSPACE_ID),
        where("startAt", "<=", until),
        orderBy("startAt", "desc"),
        fbLimit(limitTo),
      ),
    [until, limitTo],
  );
}

export function useEvent(eventId: string | undefined) {
  const [event, setEvent] = useState<CrmEvent | null>(null);
  const [loading, setLoading] = useState(Boolean(eventId));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!eventId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    return onSnapshot(
      doc(db, "events", eventId),
      (snap) => {
        setEvent(snap.exists() ? ({ id: snap.id, ...snap.data() } as CrmEvent) : null);
        setLoading(false);
        setError(null);
      },
      (err) => {
        setEvent(null);
        setLoading(false);
        setError(err.message);
      },
    );
  }, [eventId]);

  return { event, loading, error };
}

export interface EventRegistration extends DocumentData {
  id: string;
  primaryPersonId?: string;
  eventId?: string | null;
  eventSlug?: string | null;
  guestCounts?: { adults?: number; children?: number };
  answers?: { notes?: string };
  status?: string;
  /** null means nobody has marked either way yet, which is different from "did
   *  not come" — the same distinction the presence spec insists on. */
  attended?: boolean | null;
  createdAt?: Timestamp;
}

/** One event's sign-ups, in the order they arrived — which is the order a
 *  waiting list has to respect. */
export function useEventRegistrations(eventId: string | undefined) {
  const state = useQ<EventRegistration>(
    () =>
      query(
        collection(db, "registrations"),
        where("workspaceId", "==", WORKSPACE_ID),
        where("eventId", "==", eventId ?? "__none__"),
        orderBy("createdAt", "asc"),
        fbLimit(400),
      ),
    [eventId],
  );

  return eventId ? state : { data: [], loading: false, error: null };
}

/** Registrations that arrived before their event existed. Not an error state —
 *  the form goes live before anybody opens the CRM to create the event. */
export function useUnlinkedRegistrations() {
  return useQ<EventRegistration>(
    () =>
      query(
        collection(db, "registrations"),
        where("workspaceId", "==", WORKSPACE_ID),
        where("eventId", "==", null),
        fbLimit(200),
      ),
    [],
  );
}

/** Jerusalem-local day difference, so "today" and "tomorrow" mean what a person
 *  in Israel means by them. */
export function daysFromNow(ts: Timestamp | undefined | null): number | null {
  if (!ts?.toDate) return null;
  const then = ts.toDate();
  const startOfThen = new Date(then);
  startOfThen.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((startOfThen.getTime() - today.getTime()) / 86_400_000);
}

export function eventWhenText(ev: CrmEvent): string {
  const d = ev.startAt?.toDate?.();
  if (!d) return "";
  const days = daysFromNow(ev.startAt);
  const date = d.toLocaleDateString("he-IL", { weekday: "long", day: "numeric", month: "long" });
  const time = ev.allDay
    ? null
    : d.toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" });
  const rel = days === 0 ? "היום" : days === 1 ? "מחר" : days === -1 ? "אתמול" : null;
  return [rel, date, time].filter(Boolean).join(" · ");
}
